import { useState, useCallback, useMemo } from "react";
import { compressImage } from "@components/Common/Function";

/**
 * useNewFeedbackForm Hook
 * Custom hook for managing NewFeedback form state and logic
 * Optimized for performance with proper memoization
 * 
 * @param {string} initialTab - Initial tab value  
 * @param {string} userType - User type (unused but kept for API compatibility)
 * @returns {object} Form state and handlers
 */
export const useNewFeedbackForm = (initialTab = "Dealer") => {
  const [tab, setTab] = useState(initialTab);
  const [feedback, setFeedback] = useState({
    Title: "",
    Observation: "",
    dealerId: "",
    territory: "",
    clusterCode: "",
    rmdmWss: "",
  });
  const [file, setFile] = useState({ fileName: "", base64: "" });
  const [formValidation, setFormValidation] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const [cluster, setCluster] = useState(null);
  const [territory, setTerritory] = useState(null);
  const [wssDealerOrProduct, setWssDealerOrProduct] = useState(null);
  const [rmdmWssValue, setRmdmWssValue] = useState(null);

  /**
   * Handle tab change - resets all form state
   */
  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
    setFeedback({
      Title: "",
      Observation: "",
      dealerId: "",
      territory: "",
      clusterCode: "",
      rmdmWss: "",
    });
    setFile({ fileName: "", base64: "" });
    setFormValidation({});
    setCluster(null);
    setTerritory(null);
    setWssDealerOrProduct(null);
    setRmdmWssValue(null);
    setIsSubmit(false);
  }, []); // No dependencies needed - all setters are stable

  /**
   * Handle form field changes - memoized to prevent recreation
   */
  const onChange = useCallback((e, value, type) => {
    if (type === "territory") {
      setFeedback((prev) => ({
        ...prev,
        territory: value?.SH2_Code__c || "",
      }));
    } else if (type === "dealerId" && value) {
      setFeedback((prev) => ({
        ...prev,
        dealerId: value?.DealerCode__c || value?.Customer_Code__c || value?.Product_Group_3_Description__c || "",
      }));
    } else if (type === "rmdmWss") {
      setFeedback((prev) => ({
        ...prev,
        rmdmWss: value || "",
      }));
    } else if (e?.target) {
      setFeedback((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }
  }, []);

  /**
   * Handle file upload with compression
   * Optimized to prevent unnecessary re-renders
   */
  const fileHandler = useCallback(async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    const fileSizeInKB = uploadedFile.size / 1024;
    const fileName = uploadedFile.name;
    const isPDF = uploadedFile.type === "application/pdf";

    // Check file size for PDFs
    if (isPDF && fileSizeInKB > 5000) {
      setFormValidation((prev) => ({
        ...prev,
        file: "Please upload the file whose size is less than 5MB",
      }));
      return;
    }

    // Process file
    const fileToProcess = isPDF ? uploadedFile : await compressImage(uploadedFile).catch(err => {
      console.error("Error compressing image:", err);
      return uploadedFile; // Fallback to original if compression fails
    });

    // Read and set file
    const reader = new FileReader();
    reader.onload = () => {
      setFile({ fileName, base64: reader.result });
      setFormValidation((prev) => ({ ...prev, file: "" }));
    };
    reader.readAsDataURL(fileToProcess);
  }, []);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(() => ({
    tab,
    feedback,
    file,
    formValidation,
    isSubmit,
    cluster,
    territory,
    wssDealerOrProduct,
    rmdmWssValue,
    setTab,
    setFeedback,
    setFile,
    setFormValidation,
    setIsSubmit,
    setCluster,
    setTerritory,
    setWssDealerOrProduct,
    setRmdmWssValue,
    handleTabChange,
    onChange,
    fileHandler,
  }), [
    tab, feedback, file, formValidation, isSubmit,
    cluster, territory, wssDealerOrProduct, rmdmWssValue,
    handleTabChange, onChange, fileHandler
  ]);
};